import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  CollapsibleSection, 
  PastCompletedOrdersSection,
  AnusthanScheduleSection,
  BhojDaanScheduleSection,
  AstroAnalysisUploadSection
} from '../components/admin/SharedComponents';
import { BraceletConfigSection, RecommendAnusthanSection } from '../components/admin/BraceletAndRecommendComponents';
import { OrderExpandedView } from '../components/admin/OrderExpandedView';
import AdminBlogManager from '../components/AdminBlogManager';
import {
  NewOrdersTab,
  InProgressOrdersTab,
  ScheduledAnusthansTab,
  ScheduledBhojDaanTab,
  RecommendedAnusthansTab,
  AvailableAnusthansTab,
  RevenueTab,
  AnalyticsTab,
  ArchiveTab
} from '../components/admin/DashboardTabs';
import '../styles/AdminDashboard.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ══════════════════════════════════════════
// ADMIN DASHBOARD - MAIN COMPONENT
// ══════════════════════════════════════════

const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const [token, setToken] = useState(localStorage.getItem('admin_token'));
  const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem('admin_token'));
  const [loading, setLoading] = useState(true);
  
  // Tab state
  const [activeTab, setActiveTab] = useState('new-orders');
  
  // Data state
  const [newOrders, setNewOrders] = useState([]);
  const [inProgressOrders, setInProgressOrders] = useState([]);
  const [scheduledAnusthans, setScheduledAnusthans] = useState([]);
  const [scheduledBhojDaan, setScheduledBhojDaan] = useState([]);
  const [recommendedAnusthans, setRecommendedAnusthans] = useState([]);
  const [missedRecommendations, setMissedRecommendations] = useState([]);
  const [archivedAnusthans, setArchivedAnusthans] = useState([]);
  const [archivedAstroAnalysis, setArchivedAstroAnalysis] = useState([]);
  const [archivedBhojDaan, setArchivedBhojDaan] = useState([]);
  const [pastCustomers, setPastCustomers] = useState([]);
  const [revenueData, setRevenueData] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [anusthans, setAnusthans] = useState([]);
  const [availableAnusthans, setAvailableAnusthans] = useState([]);
  const [stats, setStats] = useState({ newOrders: 0, inProgress: 0, scheduledAnusthans: 0, scheduledBhojDaan: 0, recommendedAnusthans: 0, missedRecommendations: 0, archivedAnusthans: 0, archivedAstro: 0, archivedBhojDaan: 0, pastCustomers: 0, availableAnusthans: 0 });

  // Fetch with auth helper
  const fetchWithAuth = useCallback(async (url, options = {}) => {
    const headers = {
      ...options.headers,
      'Authorization': `Bearer ${token}`
    };
    const response = await fetch(url, { ...options, headers });
    if (response.status === 401) {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_username');
      setToken(null);
      setIsLoggedIn(false);
      navigate('/admin');
    }
    return response;
  }, [token, navigate]);

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_username');
    setToken(null);
    setIsLoggedIn(false);
    navigate('/admin');
    toast.info('Logged out successfully');
  };

  // Fetch new orders
  const fetchNewOrders = useCallback(async () => {
    try {
      const response = await fetchWithAuth(`${API_URL}/api/admin/new-orders`);
      const data = await response.json();
      setNewOrders(data.orders || []);
      setStats(prev => ({ ...prev, newOrders: data.count || 0 }));
    } catch (err) {
      console.error('Error fetching new orders:', err);
    }
  }, [fetchWithAuth]);

  // Fetch archived customers
  const fetchArchivedCustomers = useCallback(async () => {
    try {
      // Fetch archived Anusthans
      const anusthanResponse = await fetchWithAuth(`${API_URL}/api/admin/archived-anusthans`);
      const anusthanData = await anusthanResponse.json();
      setArchivedAnusthans(anusthanData.orders || []);
      
      // Fetch archived Astro Analysis
      const astroResponse = await fetchWithAuth(`${API_URL}/api/admin/archived-astro-analysis`);
      const astroData = await astroResponse.json();
      setArchivedAstroAnalysis(astroData.orders || []);
      
      // Fetch archived Bhoj & Daan
      const bhojDaanResponse = await fetchWithAuth(`${API_URL}/api/admin/archived-bhoj-daan`);
      const bhojDaanData = await bhojDaanResponse.json();
      setArchivedBhojDaan(bhojDaanData.orders || []);
      
      setStats(prev => ({ 
        ...prev, 
        archivedAnusthans: anusthanData.count || 0,
        archivedAstro: astroData.count || 0,
        archivedBhojDaan: bhojDaanData.total || 0
      }));
    } catch (err) {
      console.error('Error fetching archived orders:', err);
    }
  }, [fetchWithAuth]);

  // Fetch in-progress astro analysis orders
  const fetchInProgressOrders = useCallback(async () => {
    try {
      const response = await fetchWithAuth(`${API_URL}/api/admin/in-progress-astro-orders`);
      const data = await response.json();
      setInProgressOrders(data.orders || []);
      setStats(prev => ({ ...prev, inProgress: data.count || 0 }));
    } catch (err) {
      console.error('Error fetching in-progress orders:', err);
    }
  }, [fetchWithAuth]);

  // Fetch scheduled anusthans
  const fetchScheduledAnusthans = useCallback(async () => {
    try {
      const response = await fetchWithAuth(`${API_URL}/api/admin/scheduled-anusthans`);
      const data = await response.json();
      setScheduledAnusthans(data.groups || []);
      setStats(prev => ({ ...prev, scheduledAnusthans: data.total_groups || 0 }));
    } catch (err) {
      console.error('Error fetching scheduled anusthans:', err);
    }
  }, [fetchWithAuth]);

  // Fetch scheduled Bhoj & Daan
  const fetchScheduledBhojDaan = useCallback(async () => {
    try {
      const response = await fetchWithAuth(`${API_URL}/api/admin/scheduled-bhoj-daan`);
      const data = await response.json();
      setScheduledBhojDaan(data.groups || []);
      setStats(prev => ({ ...prev, scheduledBhojDaan: data.total_groups || 0 }));
    } catch (err) {
      console.error('Error fetching scheduled Bhoj & Daan:', err);
    }
  }, [fetchWithAuth]);

  // Fetch recommended anusthans
  const fetchRecommendedAnusthans = useCallback(async () => {
    try {
      const response = await fetchWithAuth(`${API_URL}/api/admin/recommended-anusthans`);
      const data = await response.json();
      setRecommendedAnusthans(data.groups || []);
      setStats(prev => ({ ...prev, recommendedAnusthans: data.total_customers || 0 }));
    } catch (err) {
      console.error('Error fetching recommended anusthans:', err);
    }
  }, [fetchWithAuth]);

  // Fetch past customers with completed orders
  const fetchPastCustomers = useCallback(async () => {
    try {
      const response = await fetchWithAuth(`${API_URL}/api/admin/past-customers`);
      const data = await response.json();
      setPastCustomers(data.customers || []);
      setStats(prev => ({ ...prev, pastCustomers: data.count || 0 }));
    } catch (err) {
      console.error('Error fetching past customers:', err);
    }
  }, [fetchWithAuth]);

  // Fetch missed recommendations
  const fetchMissedRecommendations = useCallback(async () => {
    try {
      const response = await fetchWithAuth(`${API_URL}/api/admin/missed-recommended-anusthans`);
      const data = await response.json();
      setMissedRecommendations(data.missed_recommendations || []);
      setStats(prev => ({ ...prev, missedRecommendations: data.count || 0 }));
    } catch (err) {
      console.error('Error fetching missed recommendations:', err);
    }
  }, [fetchWithAuth]);

  // Fetch revenue data
  const fetchRevenueData = useCallback(async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.service_type) params.append('service_type', filters.service_type);
      if (filters.customer_id) params.append('customer_id', filters.customer_id);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.group_by) params.append('group_by', filters.group_by);
      
      const url = `${API_URL}/api/admin/revenue${params.toString() ? '?' + params.toString() : ''}`;
      const response = await fetchWithAuth(url);
      const data = await response.json();
      setRevenueData(data);
    } catch (err) {
      console.error('Error fetching revenue:', err);
    }
  }, [fetchWithAuth]);

  // Fetch analytics data
  const fetchAnalyticsData = useCallback(async () => {
    try {
      const response = await fetchWithAuth(`${API_URL}/api/admin/analytics`);
      const data = await response.json();
      setAnalyticsData(data);
    } catch (err) {
      console.error('Error fetching analytics:', err);
    }
  }, [fetchWithAuth]);

  // Fetch anusthans for recommendations
  const fetchAnusthans = useCallback(async () => {
    try {
      const response = await fetchWithAuth(`${API_URL}/api/anusthans`);
      const data = await response.json();
      setAnusthans(data.anusthans || []);
    } catch (err) {
      console.error('Error fetching anusthans:', err);
    }
  }, [fetchWithAuth]);

  // Fetch available anusthans for management
  const fetchAvailableAnusthans = useCallback(async () => {
    try {
      const response = await fetchWithAuth(`${API_URL}/api/admin/anusthans`);
      const data = await response.json();
      setAvailableAnusthans(data.anusthans || []);
      setStats(prev => ({ ...prev, availableAnusthans: data.count || 0 }));
    } catch (err) {
      console.error('Error fetching available anusthans:', err);
    }
  }, [fetchWithAuth]);

  // Create new anusthan
  const createAnusthan = async (anusthanData) => {
    try {
      const response = await fetchWithAuth(`${API_URL}/api/admin/anusthans`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(anusthanData)
      });
      if (response.ok) {
        toast.success('Anusthan created successfully');
        fetchAvailableAnusthans();
        return true;
      }
      toast.error('Failed to create anusthan');
      return false;
    } catch (err) {
      console.error('Error creating anusthan:', err);
      toast.error('Error creating anusthan');
      return false;
    }
  };

  // Update anusthan
  const updateAnusthan = async (anusthanId, anusthanData) => {
    try {
      const response = await fetchWithAuth(`${API_URL}/api/admin/anusthans/${anusthanId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(anusthanData)
      });
      if (response.ok) {
        toast.success('Anusthan updated successfully');
        fetchAvailableAnusthans();
        return true;
      }
      toast.error('Failed to update anusthan');
      return false;
    } catch (err) {
      console.error('Error updating anusthan:', err);
      toast.error('Error updating anusthan');
      return false;
    }
  };

  // Duplicate anusthan
  const duplicateAnusthan = async (anusthanId) => {
    try {
      const response = await fetchWithAuth(`${API_URL}/api/admin/anusthans/${anusthanId}/duplicate`, {
        method: 'POST'
      });
      if (response.ok) {
        toast.success('Anusthan duplicated successfully');
        fetchAvailableAnusthans();
        return true;
      }
      toast.error('Failed to duplicate anusthan');
      return false;
    } catch (err) {
      console.error('Error duplicating anusthan:', err);
      toast.error('Error duplicating anusthan');
      return false;
    }
  };

  // Delete (deactivate) anusthan
  const deleteAnusthan = async (anusthanId) => {
    try {
      const response = await fetchWithAuth(`${API_URL}/api/admin/anusthans/${anusthanId}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        toast.success('Anusthan deactivated');
        fetchAvailableAnusthans();
        return true;
      }
      toast.error('Failed to delete anusthan');
      return false;
    } catch (err) {
      console.error('Error deleting anusthan:', err);
      toast.error('Error deleting anusthan');
      return false;
    }
  };

  // Upload analysis document
  const uploadAnalysisDocument = async (orderId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await fetchWithAuth(`${API_URL}/api/admin/orders/${orderId}/upload-analysis`, {
        method: 'POST',
        body: formData
      });
      if (response.ok) {
        toast.success('Analysis document uploaded successfully');
        fetchNewOrders();
        return true;
      } else {
        toast.error('Failed to upload document');
        return false;
      }
    } catch (err) {
      console.error('Error uploading document:', err);
      toast.error('Error uploading document');
      return false;
    }
  };

  // Remove analysis document
  const removeAnalysisDocument = async (orderId) => {
    try {
      const response = await fetchWithAuth(`${API_URL}/api/admin/orders/${orderId}/remove-analysis`, {
        method: 'DELETE'
      });
      if (response.ok) {
        toast.success('Document removed. Status reset to "Analysis Not Started"');
        fetchNewOrders();
        return true;
      } else {
        toast.error('Failed to remove document');
        return false;
      }
    } catch (err) {
      console.error('Error removing document:', err);
      toast.error('Error removing document');
      return false;
    }
  };

  // Update astro analysis status
  const updateAstroAnalysisStatus = async (orderId, status) => {
    try {
      const response = await fetchWithAuth(`${API_URL}/api/admin/orders/${orderId}/astro-analysis-status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      if (response.ok) {
        toast.success(`Analysis status updated to "${status === 'in_progress' ? 'In Progress' : status}". Order moved to In Progress tab.`);
        fetchNewOrders();
        fetchInProgressOrders();
        return true;
      } else {
        toast.error('Failed to update analysis status');
        return false;
      }
    } catch (err) {
      console.error('Error updating analysis status:', err);
      toast.error('Error updating status');
      return false;
    }
  };

  // Save bracelet configuration
  const saveBraceletConfig = async (orderId, config, status) => {
    try {
      const response = await fetchWithAuth(`${API_URL}/api/admin/orders/${orderId}/bracelet-config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bracelet_config: config, bracelet_status: status })
      });
      if (response.ok) {
        toast.success('Bracelet configuration saved');
        fetchNewOrders();
        fetchInProgressOrders();
        return true;
      } else {
        toast.error('Failed to save bracelet configuration');
        return false;
      }
    } catch (err) {
      console.error('Error saving bracelet config:', err);
      toast.error('Error saving configuration');
      return false;
    }
  };

  // Schedule Anusthan order
  const scheduleAnusthan = async (orderId) => {
    try {
      const response = await fetchWithAuth(`${API_URL}/api/admin/orders/${orderId}/schedule-anusthan`, {
        method: 'PUT'
      });
      if (response.ok) {
        toast.success('Anusthan scheduled successfully');
        fetchNewOrders();
        fetchScheduledAnusthans();
        return true;
      } else {
        toast.error('Failed to schedule Anusthan');
        return false;
      }
    } catch (err) {
      console.error('Error scheduling Anusthan:', err);
      toast.error('Error scheduling Anusthan');
      return false;
    }
  };

  // Schedule Bhoj & Daan order
  const scheduleBhojDaan = async (orderId) => {
    try {
      const response = await fetchWithAuth(`${API_URL}/api/admin/bhoj-daan/schedule`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order_id: orderId })
      });
      if (response.ok) {
        toast.success('Bhoj & Daan scheduled successfully');
        fetchNewOrders();
        fetchScheduledBhojDaan();
        return true;
      } else {
        toast.error('Failed to schedule Bhoj & Daan');
        return false;
      }
    } catch (err) {
      console.error('Error scheduling Bhoj & Daan:', err);
      toast.error('Error scheduling Bhoj & Daan');
      return false;
    }
  };

  // Send video URL to Bhoj & Daan customers
  const sendBhojDaanVideoUrl = async (orderIds, videoUrl) => {
    try {
      const response = await fetchWithAuth(`${API_URL}/api/admin/bhoj-daan/send-video-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order_ids: orderIds, video_url: videoUrl })
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchScheduledBhojDaan();
        fetchArchivedCustomers();
        return true;
      } else {
        toast.error('Failed to send video URL');
        return false;
      }
    } catch (err) {
      console.error('Error sending video URL:', err);
      toast.error('Error sending video URL');
      return false;
    }
  };

  // Send video URL to customers
  const sendVideoUrlToCustomers = async (orderIds, videoUrl) => {
    try {
      const response = await fetchWithAuth(`${API_URL}/api/admin/anusthans/send-video-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order_ids: orderIds, video_url: videoUrl })
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchScheduledAnusthans();
        fetchArchivedCustomers();
        return true;
      } else {
        toast.error('Failed to send video URL');
        return false;
      }
    } catch (err) {
      console.error('Error sending video URL:', err);
      toast.error('Error sending video URL');
      return false;
    }
  };

  // Save recommended anusthans
  const saveRecommendedAnusthans = async (orderId, recommendations) => {
    try {
      const response = await fetchWithAuth(`${API_URL}/api/admin/orders/${orderId}/recommendations`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recommendations })
      });
      if (response.ok) {
        toast.success('Recommendations saved successfully');
        fetchNewOrders();
        fetchInProgressOrders();
        fetchRecommendedAnusthans();
        fetchArchivedCustomers();
        return true;
      } else {
        toast.error('Failed to save recommendations');
        return false;
      }
    } catch (err) {
      console.error('Error saving recommendations:', err);
      toast.error('Error saving recommendations');
      return false;
    }
  };

  // Send anusthan reminder to customers
  const sendAnusthanReminder = async (orderIds, anusthanName, ceremonyDate) => {
    try {
      const response = await fetchWithAuth(`${API_URL}/api/admin/recommended-anusthans/send-reminder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order_ids: orderIds, anusthan_name: anusthanName, ceremony_date: ceremonyDate })
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || 'Reminder sent successfully');
        return true;
      } else {
        toast.error('Failed to send reminder');
        return false;
      }
    } catch (err) {
      console.error('Error sending reminder:', err);
      toast.error('Error sending reminder');
      return false;
    }
  };

  // Update order status
  const updateOrderStatus = async (orderId, status) => {
    try {
      const response = await fetchWithAuth(`${API_URL}/api/admin/orders/${orderId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      if (response.ok) {
        toast.success('Order status updated');
        fetchNewOrders();
        fetchArchivedCustomers();
        return true;
      }
      return false;
    } catch (err) {
      console.error('Error updating order:', err);
      return false;
    }
  };

  // Load data on auth
  useEffect(() => {
    if (token) {
      const loadData = async () => {
        setLoading(true);
        await Promise.all([
          fetchNewOrders(), 
          fetchInProgressOrders(), 
          fetchScheduledAnusthans(),
          fetchScheduledBhojDaan(),
          fetchRecommendedAnusthans(),
          fetchArchivedCustomers(),
          fetchPastCustomers(),
          fetchMissedRecommendations(),
          fetchRevenueData(),
          fetchAnalyticsData(),
          fetchAnusthans(),
          fetchAvailableAnusthans()
        ]);
        setLoading(false);
      };
      loadData();
    } else {
      // Redirect to login if not authenticated
      navigate('/admin');
    }
  }, [token, fetchNewOrders, fetchInProgressOrders, fetchScheduledAnusthans, fetchScheduledBhojDaan, fetchRecommendedAnusthans, fetchArchivedCustomers, fetchPastCustomers, fetchMissedRecommendations, fetchRevenueData, fetchAnalyticsData, fetchAnusthans, fetchAvailableAnusthans, navigate]);

  // Show loading while checking auth
  if (!isLoggedIn || loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#FAF7F3', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontFamily: 'Cinzel, serif', fontSize: '1.5rem', color: '#2E221A', marginBottom: '16px' }}>AMATYA ADMIN</div>
          <div style={{ fontFamily: 'Lato, sans-serif', color: '#725b47' }}>Loading...</div>
        </div>
      </div>
    );
  }

  // ══════════════════════════════════════════
  // MAIN DASHBOARD
  // ══════════════════════════════════════════
  return (
    <div className="admin-dashboard" style={{ minHeight: '100vh', background: '#FAF7F3' }}>
      {/* Header */}
      <header className="admin-header" style={{ background: '#2E221A', padding: '16px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 className="admin-header-title" style={{ fontFamily: 'Cinzel, serif', fontSize: '1.4rem', color: '#D4AF37', margin: 0 }}>
            AMATYA ADMIN
          </h1>
          <span style={{ fontFamily: 'Lato, sans-serif', fontSize: '0.75rem', color: '#a89882' }}>admin</span>
        </div>
        <button
          onClick={handleLogout}
          className="admin-logout-btn"
          style={{ padding: '10px 24px', background: 'transparent', border: '1px solid #D4AF37', color: '#D4AF37', fontFamily: 'Lato, sans-serif', fontSize: '0.8rem', letterSpacing: '0.1em', cursor: 'pointer' }}
        >
          LOGOUT
        </button>
      </header>

      {/* Stats Overview */}
      <div style={{ padding: '24px 32px', background: '#f5efe6', borderBottom: '1px solid #e0d5c5' }} className="admin-main-content">
        <div className="admin-dashboard-tiles" style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
          <div 
            onClick={() => setActiveTab('new-orders')}
            className="admin-tile"
            style={{ 
              flex: '1', 
              minWidth: '160px', 
              background: '#8B4513', 
              padding: '20px 24px', 
              cursor: 'pointer',
              transition: 'transform 0.2s',
              border: activeTab === 'new-orders' ? '3px solid #D4AF37' : '3px solid transparent'
            }}
          >
            <div className="admin-tile-label" style={{ fontFamily: 'Lato, sans-serif', fontSize: '0.7rem', color: '#D4AF37', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '8px' }}>
              New Orders
            </div>
            <div className="admin-tile-value" style={{ fontFamily: 'Cinzel, serif', fontSize: '2.5rem', color: '#FAF7F3' }}>
              {stats.newOrders}
            </div>
          </div>
          
          <div 
            onClick={() => setActiveTab('in-progress')}
            className="admin-tile"
            style={{ 
              flex: '1', 
              minWidth: '160px', 
              background: '#1565C0', 
              padding: '20px 24px', 
              cursor: 'pointer',
              transition: 'transform 0.2s',
              border: activeTab === 'in-progress' ? '3px solid #D4AF37' : '3px solid transparent'
            }}
          >
            <div className="admin-tile-label" style={{ fontFamily: 'Lato, sans-serif', fontSize: '0.7rem', color: '#90CAF9', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '8px' }}>
              In Progress Astro
            </div>
            <div className="admin-tile-value" style={{ fontFamily: 'Cinzel, serif', fontSize: '2.5rem', color: '#FAF7F3' }}>
              {stats.inProgress}
            </div>
          </div>
          
          <div 
            onClick={() => setActiveTab('scheduled-anusthans')}
            className="admin-tile"
            style={{ 
              flex: '1', 
              minWidth: '160px', 
              background: '#7B1FA2', 
              padding: '20px 24px', 
              cursor: 'pointer',
              transition: 'transform 0.2s',
              border: activeTab === 'scheduled-anusthans' ? '3px solid #D4AF37' : '3px solid transparent'
            }}
          >
            <div className="admin-tile-label" style={{ fontFamily: 'Lato, sans-serif', fontSize: '0.7rem', color: '#E1BEE7', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '8px' }}>
              Scheduled Anusthans
            </div>
            <div className="admin-tile-value" style={{ fontFamily: 'Cinzel, serif', fontSize: '2.5rem', color: '#FAF7F3' }}>
              {stats.scheduledAnusthans}
            </div>
          </div>

          <div 
            onClick={() => setActiveTab('scheduled-bhoj-daan')}
            className="admin-tile"
            style={{ 
              flex: '1', 
              minWidth: '160px', 
              background: '#00695C', 
              padding: '20px 24px', 
              cursor: 'pointer',
              transition: 'transform 0.2s',
              border: activeTab === 'scheduled-bhoj-daan' ? '3px solid #D4AF37' : '3px solid transparent'
            }}
          >
            <div className="admin-tile-label" style={{ fontFamily: 'Lato, sans-serif', fontSize: '0.7rem', color: '#B2DFDB', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '8px' }}>
              Scheduled Bhoj & Daan
            </div>
            <div className="admin-tile-value" style={{ fontFamily: 'Cinzel, serif', fontSize: '2.5rem', color: '#FAF7F3' }}>
              {stats.scheduledBhojDaan}
            </div>
          </div>

          <div 
            onClick={() => setActiveTab('recommended-anusthans')}
            className="admin-tile"
            style={{ 
              flex: '1', 
              minWidth: '160px', 
              background: '#00897B', 
              padding: '20px 24px', 
              cursor: 'pointer',
              transition: 'transform 0.2s',
              border: activeTab === 'recommended-anusthans' ? '3px solid #D4AF37' : '3px solid transparent'
            }}
          >
            <div className="admin-tile-label" style={{ fontFamily: 'Lato, sans-serif', fontSize: '0.7rem', color: '#B2DFDB', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '8px' }}>
              Recommended Anusthan
            </div>
            <div className="admin-tile-value" style={{ fontFamily: 'Cinzel, serif', fontSize: '2.5rem', color: '#FAF7F3' }}>
              {stats.recommendedAnusthans}
            </div>
          </div>
          
          <div 
            onClick={() => setActiveTab('archive')}
            className="admin-tile"
            style={{ 
              flex: '1', 
              minWidth: '160px', 
              background: '#4A3324', 
              padding: '20px 24px', 
              cursor: 'pointer',
              transition: 'transform 0.2s',
              border: activeTab === 'archive' ? '3px solid #D4AF37' : '3px solid transparent'
            }}
          >
            <div className="admin-tile-label" style={{ fontFamily: 'Lato, sans-serif', fontSize: '0.7rem', color: '#a89882', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '8px' }}>
              Archive
            </div>
            <div className="admin-tile-value" style={{ fontFamily: 'Cinzel, serif', fontSize: '2.5rem', color: '#FAF7F3' }}>
              {stats.archivedAnusthans + stats.archivedAstro}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="admin-tab-nav" style={{ display: 'flex', borderBottom: '1px solid #e0d5c5', background: '#FAF7F3', flexWrap: 'wrap', overflowX: 'auto' }}>
        {[
          { id: 'new-orders', label: 'New Orders', badge: stats.newOrders, color: '#C05C34' },
          { id: 'in-progress', label: 'In-Progress Astro Analysis', badge: stats.inProgress, color: '#1565C0' },
          { id: 'scheduled-anusthans', label: 'Scheduled Anusthans', badge: stats.scheduledAnusthans, color: '#7B1FA2' },
          { id: 'scheduled-bhoj-daan', label: 'Scheduled Bhoj & Daan', badge: stats.scheduledBhojDaan, color: '#00695C' },
          { id: 'recommended-anusthans', label: 'Recommended Anusthans', badge: stats.recommendedAnusthans, color: '#00897B' },
          { id: 'available-anusthans', label: 'List of Available Anusthan', badge: stats.availableAnusthans, color: '#FF5722' },
          { id: 'revenue', label: 'Revenue', badge: 0, color: '#FF9800' },
          { id: 'analytics', label: 'Analytics', badge: 0, color: '#607D8B' },
          { id: 'archive', label: 'Archive', badge: stats.archivedAnusthans + stats.archivedAstro + stats.archivedBhojDaan + stats.pastCustomers + (stats.missedRecommendations || 0), color: '#757575' },
          { id: 'blogs', label: 'Blogs', badge: 0, color: '#4CAF50' }
        ].map(tab => (
          <button
            key={tab.id}
            data-testid={`tab-${tab.id}`}
            className="admin-tab-btn"
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '16px 24px',
              fontFamily: 'Lato, sans-serif',
              fontSize: '0.8rem',
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              background: 'transparent',
              border: 'none',
              borderBottom: activeTab === tab.id ? '3px solid #8B4513' : '3px solid transparent',
              color: activeTab === tab.id ? '#2E221A' : '#725b47',
              cursor: 'pointer',
              fontWeight: activeTab === tab.id ? '600' : '400',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              whiteSpace: 'nowrap',
              flexShrink: 0
            }}
          >
            {tab.label}
            {tab.badge > 0 && (
              <span className="status-badge" style={{ 
                background: tab.color, 
                color: '#fff', 
                padding: '2px 8px', 
                borderRadius: '10px', 
                fontSize: '0.7rem' 
              }}>
                {tab.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="admin-main-content" style={{ padding: '24px 32px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px' }}>
            <div style={{ fontFamily: 'Cinzel, serif', fontSize: '1.2rem', color: '#725b47' }}>Loading...</div>
          </div>
        ) : (
          <>
            {activeTab === 'new-orders' && (
              <NewOrdersTab 
                orders={newOrders}
                anusthans={anusthans}
                onRefresh={fetchNewOrders}
                onUploadAnalysis={uploadAnalysisDocument}
                onRemoveAnalysis={removeAnalysisDocument}
                onUpdateAstroStatus={updateAstroAnalysisStatus}
                onSaveBraceletConfig={saveBraceletConfig}
                onSaveRecommendations={saveRecommendedAnusthans}
                onUpdateStatus={updateOrderStatus}
                onScheduleAnusthan={scheduleAnusthan}
                onScheduleBhojDaan={scheduleBhojDaan}
              />
            )}
            
            {activeTab === 'in-progress' && (
              <InProgressOrdersTab 
                orders={inProgressOrders}
                anusthans={anusthans}
                onRefresh={() => { fetchInProgressOrders(); fetchNewOrders(); }}
                onUploadAnalysis={uploadAnalysisDocument}
                onRemoveAnalysis={removeAnalysisDocument}
                onUpdateAstroStatus={updateAstroAnalysisStatus}
                onSaveBraceletConfig={saveBraceletConfig}
                onSaveRecommendations={saveRecommendedAnusthans}
                onUpdateStatus={updateOrderStatus}
              />
            )}
            
            {activeTab === 'scheduled-anusthans' && (
              <ScheduledAnusthansTab 
                groups={scheduledAnusthans}
                onRefresh={fetchScheduledAnusthans}
                onSendVideoUrl={sendVideoUrlToCustomers}
              />
            )}

            {activeTab === 'scheduled-bhoj-daan' && (
              <ScheduledBhojDaanTab 
                groups={scheduledBhojDaan}
                onRefresh={fetchScheduledBhojDaan}
                onSendVideoUrl={sendBhojDaanVideoUrl}
              />
            )}

            {activeTab === 'recommended-anusthans' && (
              <RecommendedAnusthansTab 
                groups={recommendedAnusthans}
                onRefresh={fetchRecommendedAnusthans}
                onSendReminder={sendAnusthanReminder}
              />
            )}

            {activeTab === 'available-anusthans' && (
              <AvailableAnusthansTab 
                anusthans={availableAnusthans}
                onRefresh={fetchAvailableAnusthans}
                onCreate={createAnusthan}
                onUpdate={updateAnusthan}
                onDuplicate={duplicateAnusthan}
                onDelete={deleteAnusthan}
              />
            )}

            {activeTab === 'revenue' && (
              <RevenueTab 
                data={revenueData}
                onRefresh={fetchRevenueData}
              />
            )}

            {activeTab === 'analytics' && (
              <AnalyticsTab 
                data={analyticsData}
                onRefresh={fetchAnalyticsData}
              />
            )}
            
            {activeTab === 'archive' && (
              <ArchiveTab 
                archivedAnusthans={archivedAnusthans}
                archivedAstroAnalysis={archivedAstroAnalysis}
                archivedBhojDaan={archivedBhojDaan}
                pastCustomers={pastCustomers}
                missedRecommendations={missedRecommendations}
                onRefresh={() => { fetchArchivedCustomers(); fetchPastCustomers(); fetchMissedRecommendations(); }}
              />
            )}
            {activeTab === 'blogs' && (
              <AdminBlogManager token={token} />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminDashboardPage;
